import axios from 'axios';
import StaticVar from '../config/StaticVar';
import { isExpired } from 'react-jwt';

// ===> api create
const api = axios.create({
	baseURL: StaticVar.URL_API,
	headers: {
		Authorization: `Bearer ${localStorage.getItem('access_token')}`,
	},
});

const isExpiredGoogle = expiryDate => {
	return new Date().getTime() > expiryDate;
};

const removeTokensFromLocalStorage = () => {
	const tokens = [
		'access_token',
		'refresh_token',
		'google_token',
		'google_refresh_token',
		'google_id_token',
		'gmail_token',
		'gmail_id_token',
	];
	tokens.forEach(token => localStorage.removeItem(token));
};

const handleError = error => {
	removeTokensFromLocalStorage();
	window.location.href = '/login?expired';
	throw new Error('Failed to refresh token');
};

const getNewAccessToken = async refreshToken => {
	console.log('api.js getnew access token');
	try {
		const response = await axios.post(`${StaticVar.URL_API}/auth/refresh`, {
			refreshToken,
		});
		const { accessToken, refreshToken: newRefreshToken } = response.data;

		localStorage.setItem('access_token', accessToken);
		localStorage.setItem('refresh_token', newRefreshToken);

		return accessToken;
	} catch (error) {
		handleError();
	}
};

const googleRefreshToken = async () => {
	let googleToken = localStorage.getItem('google_token');
	let googleRefreshToken = localStorage.getItem('google_refresh_token');
	let googleExpiryDate = localStorage.getItem('google_expiry_date');
	console.log(
		'token Google Expired',
		isExpiredGoogle(googleExpiryDate),
		googleExpiryDate
	);
	if (googleToken && isExpiredGoogle(googleExpiryDate)) {
		try {
			const tokenResponse = await axios.post(
				`${StaticVar.URL_API}/auth/google/refresh`,
				{ refreshToken: googleRefreshToken }
			);
			const { access_token, id_token, refresh_token, expiry_date } =
				tokenResponse.data;

			localStorage.setItem('google_token', access_token);
			localStorage.setItem('google_id_token', id_token);
			localStorage.setItem('google_refresh_token', refresh_token);
			localStorage.setItem('google_expiry_date', expiry_date);

			return access_token;
		} catch (error) {
			handleError();
		}
	} else {
		return googleToken;
	}
};

const checkAndRefreshGoogleToken = async () => {
	const googleToken = await googleRefreshToken();
	if (googleToken) {
		api.defaults.headers.Authorization = `Bearer ${googleToken}`;
	}
	return googleToken;
};

api.interceptors.request.use(
	async config => {
		console.log('interceptors.request');
		// check google expired
		await checkAndRefreshGoogleToken();

		// console.log("intercept request")
		let accessToken = localStorage.getItem('access_token');
		let refreshToken = localStorage.getItem('refresh_token');

		if (isExpired(accessToken) && refreshToken) {
			accessToken = await getNewAccessToken(refreshToken);
		}

		config.headers.Authorization = `Bearer ${accessToken}`;
		return config;
	},
	error => {
		// Do something with the request error
		return Promise.reject(error);
	}
);

api.interceptors.response.use(
	response => {
		// If the request was successful, return the response
		return response;
	},
	async error => {
		console.log('interceptors.response', error);

		// check google expired
		await checkAndRefreshGoogleToken();

		const originalRequest = error.config;

		const accessToken = localStorage.getItem('access_token');
		const refreshToken = localStorage.getItem('refresh_token');

		if (error?.response?.status === 401) {
			originalRequest._retry = true;

			if (isExpired(accessToken) && refreshToken) {
				const newAccessToken = await getNewAccessToken(refreshToken);
				originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
				return api(originalRequest);
			}
		} else {
			console.log('response here', error);
			// handleError()
		}

		return Promise.reject(error);
	}
);

// api routes
// users
const getAllUser = params => api.get('/user', { params });
const getUserasFilter = params => api.get('/user/as/filter', { params });
const getUser = (id, params) => api.get(`/user/${id}`, { params });
const postUser = data => api.post('/user/register', data);
const putUser = (id, data) => api.put(`/user/${id}`, data);
const putFCM = (id, data) => api.put(`/user/fcm/${id}`, data);
const deleteUser = id => api.delete(`/user/${id}`);
const getUserReturnFCM = data => api.post('/user/fcm', data);
const changePassword = data => api.post('/user/changePassword', data);
const resetPassword = data => api.post('/user/password/reset', data);

// signature
const getAllSignature = params => api.get('/user/signature', { params });
const getSignaturebyID = (id, params) =>
	api.get('/user/signature/' + id, { params });
const getSignaturebyEmail = (email, params) =>
	api.get('/user/signature/byemail/' + email, { params });
const postSignature = data => api.post('/user/signature', data);
const putSignature = (id, data) => api.put('/user/signature/' + id, data);
const deleteSignature = id => api.delete('/user/signature/' + id);

// departement
const getAllDepartement = data => api.get('/user/departement', data);

// roles
const postRole = data => api.post('/role/add', data);
const getRoles = params => api.get('/role', { params });
const putRoleProfile = (id, data) => api.put(`/role/profile/${id}`, data);
const putChangePermission = (id, data) =>
	api.put(`/role/changePermission/${id}`, data);
const deleteRole = id => api.delete(`/role/${id}`);
const getListPermissions = () => api.get('/permission');

// companies
const getAllCompanies = params => api.get('/client/company', { params });
const getCompany = (id, params) => api.get(`/client/company/${id}`, { params });
const postCompany = data => api.post('/client/company/add', data);
const putCompany = (id, data) => api.put(`/client/company/${id}`, data);
const deleteCompany = id => api.delete(`/client/company/${id}`);

// contacts
const getAllContacts = params => api.get('/client/contact', { params });
const getContactbyPage = params => api.get('/client/contact/p/all', { params });
const getContact = (id, params) => api.get(`/client/contact/${id}`, { params });
const postContact = data => api.post('/client/contact/add', data);
const postManyContact = data => api.post('/client/contact/many', data);
const postManyContactWith = data =>
	api.post('/client/contact/many/withvalidation', data);
const putContact = (id, data) => api.put(`/client/contact/${id}`, data);
const deleteContact = id => api.delete(`/client/contact/${id}`);

// patent contact
const getListCpantent = params => api.get('/client/cpatent', { params });
const getCpatentbyPage = params => api.get('/client/cpatent/p/all', { params });
const getCPatent = (id, params) => api.get(`/client/cpatent/${id}`, { params });
const postCpatent = data => api.post('/client/cpatent/add', data);
const putCpatent = (id, data) => api.put(`/client/cpatent/${id}`, data);
const deleteCpatent = id => api.delete(`/client/cpatent/${id}`);

// patent desain industri
const getListCindustri = params => api.get('/client/cdesainindustri', { params });
const getCindustribyPage = params => api.get('/client/cdesainindustri/p/all', { params });
const getCindustri = (id, params) => api.get(`/client/cdesainindustri/${id}`, { params });
const postCindustri = data => api.post('/client/cdesainindustri/add', data);
const putCindustri = (id, data) => api.put(`/client/cdesainindustri/${id}`, data);
const deleteCindustri = id => api.delete(`/client/cdesainindustri/${id}`);

// contacts
const getDepartements = params => api.get('/departement', { params });
const postDepartement = data => api.post('/departement/add', data);
const putDepartement = (id, data) => api.put(`/departement/${id}`, data);
const deleteDepartement = id => api.delete(`/departement/${id}`);

// filling
const getFilling = params => api.get('/matter/filling', params);
const getFilingbyPage = params => api.get('/matter/filling/p/all', params);
const getFilingasFilter = params => api.get('/matter/filling/asfilter', params);
const getRecordal = params => api.get('/matter/filling/recordal', params);
const getSingleClass = params => api.get('/matter/filling/singleclass', params);
const getFilingbyID = id => api.get('/matter/filling/' + id);
const postFilling = (data, signal) =>
	api.post('/matter/filling/add', data, signal);
const putFilling = (id, data, signal) =>
	api.put(`/matter/filling/${id}`, data, signal);
const putFillingGenNewRef = (id, data, signal) =>
	api.put(`/matter/filling/gen/${id}`, data, signal);
const deleteFilling = id => api.delete(`/matter/filling/${id}`);
const updateManyFiling = data => api.post('/matter/filling/update/many', data);
const updateManyFilingForEach = data =>
	api.post('/matter/filling/update/foreach', data);
const getTaskbyUnwind = params => api.get('/matter/filling/task', params);
const getTasklistGroup = params =>
	api.get('/matter/filling/task/group', params);
const pushEventToFiling = (id, data) =>
	api.put(`/matter/filling/event/${id}`, data);
const getEventlistGroup = params =>
	api.get('/matter/filling/event/group', params);

const postMulticlass = data => api.post('/matter/filling/add/multiclass', data);
const postSingleclass = data =>
	api.post('/matter/filling/add/singleclass', data);

const filingGrouping = data => api.post('/matter/filling/grouping', data);

const postGenerateRenewal = data =>
	api.post('/matter/filling/ref/renewal', data);
const postGenerateRecordal = data =>
	api.post('/matter/filling/ref/recordal', data);
const postManualMultiClass = data =>
	api.post('/matter/filling/ref/manual', data);
const putTaskByEvent = (id, data) =>
	api.put('/matter/filling/oa/event/' + id, data);
const putStatusEvents = data =>
	api.post('/matter/filling/oa/event/status', data);
const pullInvoice = data => api.post(`/matter/filling/invoice/pull`, data);

// markbook
const getMarkBook = params => api.get('/matter/report/book', params);
const getMarkBookLimit = params => api.get('/matter/report/book/limit', params);
const getReportSearch = params => api.get('/matter/report/search', params);
const getReportOA = params => api.get('/matter/report/oa', params);
const getReportLegal = params => api.get('/matter/report/lg', params);

// events by filing OA conditions
const getfEvent = params => api.get('/matter/fevent', params);
const getfEventbyID = id => api.get('/matter/fevent/' + id);
const postfEvent = (data, signal) => api.post('/matter/fevent', data, signal);
const putManyfEvent = data => api.post('/matter/fevent/many', data);
const putfEvent = (id, data) => api.put('/matter/fevent/' + id, data);
const deletefEvent = id => api.delete('/matter/fevent/' + id);

// events by filing legal conditions
const getFlegal = params => api.get('/matter/flegal', params);
const getFlegalbyID = id => api.get('/matter/flegal/' + id);
const getFlegalbyParentID = id => api.get('/matter/flegal/parent/' + id);
const postfLegal = data => api.post('/matter/flegal', data);
const putManyfLegal = data => api.post('/matter/flegal/many', data);
const putfLegal = (id, data) => api.put('/matter/flegal/' + id, data);
const deletefLegal = id => api.delete('/matter/flegal/' + id);

// Office Action
const getOALists = params => api.get('/matter/filling/oa/list', params);
const getOAEvents = params => api.get('/matter/filling/oa/event', params);
const getOAEvenDetail = (id, params) =>
	api.get('/matter/filling/oa/event/' + id, params);
// refOA
const postRefOA = (data, signal) =>
	api.post('/matter/filling/ref/oa', data, signal);
const postRefOPO = (data, signal) =>
	api.post('/matter/filling/ref/opo', data, signal);
const putOAWithRef = (id, data) =>
	api.put('/matter/filling/update/refoa/' + id, data);

const postRefLegal = data => api.post('/matter/filling/ref/legal', data);
const putLegalWithRef = (id, data) =>
	api.put('/matter/filling/update/reflegal/' + id, data);

const postRefSearch = data => api.post('/matter/filling/ref/search', data);

// dashbaord
const getCountToday = params =>
	api.get('/matter/dashboard/today/count', params);
const getWeeklyCount = params =>
	api.get('/matter/dashboard/weekly/status', params);
const getWeeklybySub = params =>
	api.get('/matter/dashboard/weekly/sub', params);

const getCountDrafted = params =>
	api.get('/matter/dashboard/count/drafted', params);
const getCountByStatus = params =>
	api.get('/matter/dashboard/count/status', params);
const getCountbySubmitted = params =>
	api.get('/matter/dashboard/count/submitted', params);
const getCountActive = params =>
	api.get('/matter/dashboard/count/active', params);

const getFilingToday = params =>
	api.get('/matter/dashboard/list/filing', params);
const getDashboardList = params => api.get('/matter/dashboard/list', params);
// const getListFilingReport = (params) => api.get("/matter/dashboard/list/filingreport", params)
// const getListPublication = (params) => api.get("/matter/dashboard/list/publication", params)
// const getListStatus = (params) => api.get("/matter/dashboard/list/status", params)
// const getListCertificate = (params) => api.get("/matter/dashboard/list/certificate", params)
// const getListOA = params => api.get('/matter/dashboard/list/oa', params);
// const getListRenewal = (params) => api.get("/matter/dashboard/list/renewal", params)
// const getListRecordal = (params) => api.get("/matter/dashboard/list/recordal", params)
// const getDrafted = (params) => api.get("/matter/dashboard/list/drafted", params)

// new dashbaord
const getCountbyClass = params => api.get('/matter/newdash/newfiling', params);

// history
const getHistory = params => api.get('/matter/history', params);
const getHistorybyRefID = id => api.get('/matter/history/' + id);
const postHistory = data => api.post('/matter/history', data);
const putHistory = (id, data) => api.put('/matter/history/' + id, data);
const deleteHistory = id => api.delete('/matter/history/' + id);

const postDataExcel = data => api.post('/matter/filling/data/import', data);
const postDataExcelSearch = data =>
	api.post('/matter/filling/data/import/search', data);
const postDataExcelOPO = data =>
	api.post('/matter/filling/data/import/opo', data);

const postGenerateNumberReference = body =>
	api.post('/matter/filling/generateNumberReference', body);

// upload
const uploadImageBase64 = async data => api.post('/upload/imgbase', data);
const uploadDocumentBase64 = async data => {
	const token = await checkAndRefreshGoogleToken();
	let dataNew = { ...data, token };
	return api.post('/upload/docbase', dataNew);
};
const uploadDocument = async data => {
	const token = await checkAndRefreshGoogleToken();
	let dataNew = { ...data, token };
	return api.post('/upload/doc', dataNew);
};
const getFile = async params => {
	//check tokengoole
	const token = await checkAndRefreshGoogleToken();
	let paramsNew = { ...params, token };
	console.log('tokenGoogle', paramsNew);
	return api.get('/upload', { params });
};

// convert
const postConvertPdfToImg = data => api.post('/upload/pdftoimg', data);
const postConvertPdfToImgByID = data => api.post('/upload/pdftoimg/byid', data);
const postConvertImgToJsonOCR = data => api.post('/message/ocr', data);

// ocr function
const generateOCRPatent = data => api.post('/message/doc/patent', data);
const genOcrRecordalPatent = data => api.post('/message/doc/recpatent', data);
const genOcrRenewalPatent = data => api.post('/message/doc/renpatent', data);

const generateOCREfiling = data => api.post('/message/doc/efiling', data);
const generateOCRRenewal = data => api.post('/message/doc/renewal', data);
const generateOCRRecordal = data => api.post('/message/doc/recordal', data);

// master data class
const getMasterClass = params => api.get('/matter/masterclass', params);
const postMasterClass = data => api.post('/matter/masterclass/add', data);
const putMasterClass = (id, data) => api.put(`/matter/masterclass/${id}`, data);
const deleteMasterClass = id => api.delete(`/matter/masterclass/${id}`);

// master tax rate
const getTaxRate = params => api.get('/matter/tax', params);
const getTaxRatebyID = (id, params) => api.get('/matter/tax/' + id, params);
const postTaxRate = data => api.post('/matter/tax/add', data);
const putTaxRate = (id, data) => api.put(`/matter/tax/${id}`, data);
const deleteTaxRate = id => api.delete(`/matter/tax/${id}`);

// master data tasks
const getMasterTasks = params => api.get('/matter/mastertasks', params);
const getMasterTasksToFiling = params =>
	api.get('/matter/mastertasks/tofiling', params);
const getTasksToStatus = params =>
	api.get('/matter/mastertasks/tostatus', params);
const getMasterTasksToRecordal = params =>
	api.get('/matter/mastertasks/torecordal', params);
const getMasterTasksToRenewal = params =>
	api.get('/matter/mastertasks/torenewal', params);
const getTaskToOA = params => api.get('/matter/mastertasks/task/oa', params);
const postMasterTasks = data => api.post('/matter/mastertasks/add', data);
const putMasterTasks = (id, data) => api.put(`/matter/mastertasks/${id}`, data);
const deleteMasterTasks = id => api.delete(`/matter/mastertasks/${id}`);
const getTaskPatent = params => api.get('/matter/mastertasks/patent', params);

// master consultant
const getConsultants = params => api.get('/client/consultant', params);
const getDetailConsultant = (id, params) =>
	api.get(`/client/consultant/${id}`, { params });
const postConsultant = data => api.post('/client/consultant/add', data);
const putConsultant = (id, data) => api.put(`/client/consultant/${id}`, data);
const deleteConsultant = id => api.delete(`/client/consultant/${id}`);

// master event
const getMasterEvents = params => api.get('/matter/masterevent', params);
const postMasterEvent = data => api.post('/matter/masterevent/add', data);
const putMasterEvent = (id, data) => api.put(`/matter/masterevent/${id}`, data);
const deleteMasterEvent = id => api.delete(`/matter/masterevent/${id}`);

// log activity
const getActivityLog = params => api.get('/matter/activitylog', params);
const postActivityLog = data => api.post('/matter/activitylog/add', data);
const putActivityLog = (id, data) => api.put(`/matter/activitylog/${id}`, data);
const deleteActivityLog = id => api.delete(`/matter/activitylog/${id}`);

// paymentitem
const getPaymentItem = params => api.get('/matter/item', params);
const getPaymentItemPopulate = params =>
	api.get('/matter/item/populate', params);
const getPItemPopulateByID = (id, params) =>
	api.get('/matter/item/populate/' + id, params);
const getPItemGrouping = (id, params) =>
	api.get('/matter/item/grouping/' + id, params);

const postPaymentItem = data => api.post('/matter/item', data);
const putPItembyContact = data => api.post('/matter/item/cost/bycontact', data);
const postManyPaymentItem = data => api.post('/matter/item/many', data);
const putPaymentItem = (id, data) => api.put(`/matter/item/${id}`, data);
const deletePaymentItem = id => api.delete(`/matter/item/${id}`);

// desc items
const getDescItem = params => api.get('/matter/desc', params);
const postDescItem = data => api.post('/matter/desc/add', data);
const putDescItem = (id, data) => api.put(`/matter/desc/${id}`, data);
const deleteDescItem = id => api.delete(`/matter/desc/${id}`);

// Data Area
const getDataArea = params => api.get('/area', { params });

// notification
const pushNotif = data => api.post('/notif/request/send', data);
const pushNotifMulti = data => api.post('/notif/request/send/multi', data);
const sendEachForMulticast = data =>
	api.post('/notif/request/send/each/multi', data);
const getNotif = params => api.get('/notif/request', params);
const getNotifLimit = params => api.get('/notif/request/limit', params);
const getNotifByType = params => api.get('/notif/request/bytype', params);
const getBadge = params => api.get('/notif/request/badge', params);
const getNotifByID = id => api.get('/notif/request/' + id);
const postNotif = data => api.post('/notif/request/', data);
const putNotif = (id, data) => api.put(`/notif/request/${id}`, data);
const deleteNotif = id => api.delete(`/notif/request/${id}`);
const updateReceived = data => api.post('/notif/request/received', data);
const deleteManyNotif = data => api.post('/notif/request/delete/many', data);
const markReadManyNotif = data => api.post('/notif/request/read/many', data);
const subScribeToTopic = data => api.post('/notif/request/sub', data);
const unSubscribeToTopic = data => api.post('/notif/request/unsub', data);

// reminder
const getReminder = params => api.get('/notif/reminder', params);
const getReminderByID = id => api.get('/notif/reminder/' + id);
const postReminder = data => api.post('/notif/reminder/', data);
const insertManyReminder = data => api.post('/notif/reminder/many', data);
const putReminder = (id, data) =>
	api.put(`/notif/                          reminder/${id}`, data);
const deleteReminder = id => api.delete(`/notif/reminder/${id}`);

// Invoice
const getInvoices = params => api.get('/matter/invoice', params);
const getInvoiceByID = id => api.get('/matter/invoice/' + id);
const getFilingbyContact = id => api.get('/matter/invoice/filing/' + id);
const postInvoice = data => api.post('/matter/invoice/add', data);
const addTaxtoInvoiceBatch = data =>
	api.post('/matter/invoice/tax/batch', data);
const generateInvoiceNumber = data =>
	api.post('/matter/invoice/generate', data);
const putInvoice = (id, data) => api.put(`/matter/invoice/${id}`, data);
const deleteInvoice = id => api.delete(`/matter/invoice/${id}`);
const getInvoiceReport = params =>
	api.get('/matter/invoice/report/list', params);

// invoice production
const getInvbyFiling = params => api.get(`/matter/inv/filing`, params);
const getInvProdbyID = id => api.get(`/matter/inv/` + id);
const getInvProd = params => api.get(`/matter/inv`, params);
const getInvProdGenerated = params => api.get(`/matter/inv/prod`, params);
const createInvProd = data => api.post(`/matter/inv`, data);

// invoice SOA
const getInvSOA = params => api.get(`/matter/inv/soa/get`, params);
const getInvSOAbyID = id => api.get(`/matter/inv/soa/get/` + id);
const createInvSOA = data => api.post(`/matter/inv/soa`, data);
const createManyInvSOA = data => api.post(`/matter/inv/soa/many`, data);

//dashboard
const getFilingCount = params => api.get('/matter/filling/count', params);

// templates
const getTemplate = params => api.get('/matter/template', params);
const postTemplate = data => api.post('/matter/template/add', data);
const getDetailTemplate = data => api.post('/matter/template/get/detail', data);
const putTemplate = (id, data) => api.put(`/matter/template/${id}`, data);
const deleteTemplate = id => api.delete(`/matter/template/${id}`);

// PATENT
const getListPatent = params => api.get('/patent/req', params);
const getPatentByID = (id, params) => api.get('/patent/req/' + id, params);
const postPatent = data => api.post('/patent/req', data);
const postGenerateRefPatent = data => api.post('/patent/req/generate', data);
const postGenRefSearch = data => api.post('/patent/req/gensearch', data);
const putPatent = (id, data) => api.put(`/patent/req/${id}`, data);
const deletePatent = id => api.delete(`/patent/req/${id}`);
const putManyPatent = data => api.post('/patent/req/put/many', data);

const putPatentNormal = (id, data) => api.put(`/patent/req/normal/${id}`, data);
const getListApplicant = params => api.get('/patent/owner', params);

// PATENT OA
const getOAPatent = params => api.get('/patent/req/list/oa', params);
const postOAMatterPatent = data => api.post('/patent/oapatent', data);
const putManyOAMatterPatent = data => api.post('/patent/oapatent/many', data);
const getMatterPatentbyParentID = id =>
	api.get('/patent/oapatent/parent/' + id);

const createMatterPatent = (id, data) =>
	api.put('/patent/req/agenda/' + id, data);
const getMatterbyPatent = (id, params) =>
	api.get('/patent/req/agenda/' + id, params);
const updateMatterPatent = (agendaId, data) =>
	api.put('/patent/req/agenda/put/' + agendaId, data);

// TASK PATENT
const putTaskPatent = (id, data) => api.put('/patent/task/' + id, data);
const updateTaskMatterPatent = (taskId, data) =>
	api.put('/patent/task/' + taskId, data);
const extendDeadlineTask = (id, data) =>
	api.put('/patent/task/extend/' + id, data);
const getExtendHistories = id => api.get('/patent/task/extend/' + id);

// RECORDAL PATENT
const createRecordalPatent = (id, data) =>
	api.put('/patent/recordal/' + id, data);
const getRecordalPatentByID = id => api.get('/patent/recordal/' + id);

// LOG UPLOAD PATENT
const getUploadLog = params => api.get('/patent/uploadlog', params);
const postUploadLog = data => api.post('/patent/uploadlog', data);
const postUploadLogbyTask = data => api.post('/patent/uploadlog/bytask', data);
const putUploadLog = (id, data) => api.put(`/patent/uploadlog/${id}`, data);
const deleteUploadLog = id => api.delete(`/patent/uploadlog/${id}`);

// existing
const getExistRecordal = params => api.get('/patent/req/exist/rec', params);
const getExistRenewal = params => api.get('/patent/req/exist/ren', params);

// RENEWAL HISTORY
const getRenPayment = id => api.get('/patent/renpayment/parent/' + id);
const postRenPayment = data => api.post('/patent/renpayment', data);
const putRenPayment = (id, data) => api.put(`/patent/renpayment/${id}`, data);
const deleteRenPayment = id => api.delete(`/patent/renpayment/${id}`);

// ANNUAL
const getAnnuities = id => api.get('/patent/annuity/parent/' + id);
const postAnnuity = data => api.post('/patent/annuity', data);
const postManyAnnuity = data => api.post('/patent/annuity/many', data);
const putAnnuity = (id, data) => api.put(`/patent/annuity/${id}`, data);
const deleteAnnuity = id => api.delete(`/patent/annuity/${id}`);

// LOG RECORDAL PATENT
const getLogRecordals = params => api.get('/patent/logrec', params);
const getLogRecordalByParent = (id, params) =>
	api.get('/patent/logrec/parent/' + id, params);
const postLogRecordals = data => api.post('/patent/logrec', data);
const putLogRecordals = (id, data) => api.put(`/patent/logrec/${id}`, data);
const deleteLogRecordals = id => api.delete(`/patent/logrec/${id}`);

// LOG PGA PATENT
const getLogPgas = params => api.get('/patent/logpga', params);
const getLogPgabyParent = (id, params) =>
	api.get('/patent/logpga/parent/' + id, params);
const postLogPga = data => api.post('/patent/logpga', data);
const putLogPga = (id, data) => api.put(`/patent/logpga/${id}`, data);
const deleteLogPga = id => api.delete(`/patent/logpga/${id}`);

// LOG RENEWAL
const getLogRenewal = params => api.get('/patent/lgoren', params);
const getLogRenbyParent = (id, params) =>
	api.get('/patent/lgoren/parent/' + id, params);
const postLogRenewal = data => api.post('/patent/lgoren', data);

const getDefaultFees = params => api.get('/patent/fee', params);

const importBookPatent = data => api.post('/patent/migration', data);

// DASHBOARD PATENT
const getCountbyPatentType = data => api.get('/patent/dash/patentType', data);
const getTodaybySubdiv = data => api.get('/patent/dash/todaysubdiv', data);
const getChartBarSubdiv = data => api.get('/patent/dash/charsubdiv', data);
const getByStatus = data => api.get('/patent/dash/charstatus', data);
const getListDashPatent = data => api.get('/patent/dash/list', data);

const API = {
	getAllUser,
	getUserasFilter,
	getUser,
	postUser,
	putUser,
	putFCM,
	deleteUser,
	getUserReturnFCM,
	changePassword,
	resetPassword,

	getAllSignature,
	getSignaturebyID,
	getSignaturebyEmail,
	postSignature,
	putSignature,
	deleteSignature,

	getAllDepartement,

	uploadImageBase64,
	uploadDocumentBase64,
	uploadDocument,
	getFile,

	postConvertPdfToImg,
	postConvertPdfToImgByID,
	postConvertImgToJsonOCR,

	generateOCRPatent,
	genOcrRecordalPatent,
	genOcrRenewalPatent,

	generateOCREfiling,
	generateOCRRenewal,
	generateOCRRecordal,

	postRole,
	getRoles,
	putRoleProfile,
	putChangePermission,
	deleteRole,
	getListPermissions,

	getDataArea,

	getAllCompanies,
	getCompany,
	postCompany,
	putCompany,
	deleteCompany,

	getAllContacts,
	getContactbyPage,
	getContact,
	postContact,
	postManyContact,
	postManyContactWith,
	putContact,
	deleteContact,

	getConsultants,
	getDetailConsultant,
	postConsultant,
	putConsultant,
	deleteConsultant,

	getFilling,
	getFilingbyPage,
	getFilingasFilter,
	getRecordal,
	getSingleClass,
	getFilingbyID,
	postFilling,
	putFilling,
	putFillingGenNewRef,
	deleteFilling,
	updateManyFiling,
	updateManyFilingForEach,
	getTaskbyUnwind,
	getTasklistGroup,
	pushEventToFiling,
	getEventlistGroup,

	postMulticlass,
	postSingleclass,
	postGenerateRenewal,
	filingGrouping,

	postManualMultiClass,
	getOALists,
	getOAEvents,
	getOAEvenDetail,
	putTaskByEvent,
	putStatusEvents,
	pullInvoice,

	getMarkBook,
	getMarkBookLimit,
	getReportSearch,
	getReportOA,
	getReportLegal,

	getfEvent,
	getfEventbyID,
	postfEvent,
	putfEvent,
	putManyfEvent,
	deletefEvent,

	getFlegal,
	getFlegalbyID,
	getFlegalbyParentID,
	postfLegal,
	putManyfLegal,
	putfLegal,
	deletefLegal,

	getCountToday,
	getCountDrafted,
	getWeeklyCount,
	getWeeklybySub,

	getCountByStatus,
	getCountbySubmitted,
	getCountActive,

	getFilingToday,
	getDashboardList,
	// getListFilingReport,
	// getListPublication,
	// getListStatus,
	// getListCertificate,
	// getListOA,
	// getListRenewal,
	// getListRecordal,
	// getDrafted,

	getCountbyClass,

	postDataExcel,
	postDataExcelSearch,
	postDataExcelOPO,

	postGenerateNumberReference,

	getDepartements,
	postDepartement,
	putDepartement,
	deleteDepartement,

	getMasterClass,
	postMasterClass,
	putMasterClass,
	deleteMasterClass,

	getTaxRate,
	getTaxRatebyID,
	postTaxRate,
	putTaxRate,
	deleteTaxRate,

	getMasterTasks,
	getMasterTasksToFiling,
	getTasksToStatus,
	getMasterTasksToRecordal,
	getMasterTasksToRenewal,
	getTaskToOA,
	postMasterTasks,
	putMasterTasks,
	deleteMasterTasks,
	getTaskPatent,

	getMasterEvents,
	postMasterEvent,
	putMasterEvent,
	deleteMasterEvent,

	getActivityLog,
	postActivityLog,
	putActivityLog,
	deleteActivityLog,

	pushNotif,
	pushNotifMulti,
	sendEachForMulticast,
	getNotif,
	getNotifLimit,
	getNotifByType,
	getBadge,
	getNotifByID,
	postNotif,
	putNotif,
	deleteNotif,
	updateReceived,
	deleteManyNotif,
	markReadManyNotif,

	subScribeToTopic,
	unSubscribeToTopic,

	getReminder,
	getReminderByID,
	postReminder,
	insertManyReminder,
	putReminder,
	deleteReminder,

	getInvoices,
	getInvoiceByID,
	getFilingbyContact,
	postInvoice,
	addTaxtoInvoiceBatch,
	generateInvoiceNumber,
	putInvoice,
	deleteInvoice,
	getInvoiceReport,

	getInvbyFiling,
	getInvProdbyID,
	getInvProd,
	createInvProd,
	getInvProdGenerated,

	getInvSOA,
	getInvSOAbyID,
	createInvSOA,
	createManyInvSOA,

	getFilingCount,

	getTemplate,
	postTemplate,
	getDetailTemplate,
	putTemplate,
	deleteTemplate,

	postRefOA,
	postRefOPO,
	putOAWithRef,

	postRefLegal,
	putLegalWithRef,

	postRefSearch,

	getPaymentItem,
	getPaymentItemPopulate,
	getPItemPopulateByID,
	getPItemGrouping,
	postPaymentItem,
	putPItembyContact,
	postManyPaymentItem,
	putPaymentItem,
	deletePaymentItem,

	getDescItem,
	postDescItem,
	putDescItem,
	deleteDescItem,

	getHistory,
	getHistorybyRefID,
	postHistory,
	putHistory,
	deleteHistory,

	getListPatent,
	getPatentByID,
	postPatent,
	postGenerateRefPatent,
	postGenRefSearch,
	putPatent,
	deletePatent,
	getListApplicant,

	getOAPatent,

	postOAMatterPatent,
	putManyOAMatterPatent,
	getMatterPatentbyParentID,

	createMatterPatent,
	getMatterbyPatent,
	updateTaskMatterPatent,
	updateMatterPatent,

	extendDeadlineTask,
	getExtendHistories,

	createRecordalPatent,
	getRecordalPatentByID,

	putTaskPatent,
	putManyPatent,
	putPatentNormal,

	getUploadLog,
	postUploadLog,
	postUploadLogbyTask,
	putUploadLog,
	deleteUploadLog,

	getExistRecordal,
	getExistRenewal,

	getRenPayment,
	postRenPayment,
	putRenPayment,
	deleteRenPayment,

	getListCpantent,
	getCpatentbyPage,
	getCPatent,
	postCpatent,
	putCpatent,
	deleteCpatent,

	getListCindustri,
	getCindustribyPage,
	getCindustri,
	postCindustri,
	putCindustri,
	deleteCindustri,

	getLogRecordals,
	getLogRecordalByParent,
	postLogRecordals,
	putLogRecordals,
	deleteLogRecordals,

	getLogPgas,
	getLogPgabyParent,
	postLogPga,
	putLogPga,
	deleteLogPga,

	getLogRenewal,
	getLogRenbyParent,
	postLogRenewal,

	getAnnuities,
	postAnnuity,
	postManyAnnuity,
	putAnnuity,
	deleteAnnuity,

	getDefaultFees,

	importBookPatent,

	getCountbyPatentType,
	getTodaybySubdiv,
	getChartBarSubdiv,
	getByStatus,
	getListDashPatent,
};

export default API;
